import { Plus } from "lucide-react";
import Modal from "../components/Modal";
import Moo from "../features/moo/Moo";
import AddTransactionModal from "../features/entry/AddTransactionModal";
import CardStatementForm from "../features/accounts/CardStatementForm";
import ConfirmDialog from "../components/ConfirmDialog";
import ReconciliationReview from "../features/accounts/ReconciliationReview";
import PendingTransactionDetail from "../features/transactions/PendingTransactionDetail";
import TransactionDetail from "../features/transactions/TransactionDetail";
import AccountDetail from "../features/accounts/AccountDetail";
import ProfileMenu from "./ProfileMenu";
import PayBill from "../features/bills/PayBill";
import type { useEntryModal } from "../features/entry/use-entry-modal";
import type { useTransactionDetail } from "../features/transactions/use-transaction-detail";
import type {
  DashboardData,
  Owner,
  OwnerFilter,
} from "../types";
import { cardStatementStatus, isCard, todayIn } from "../lib/finance";
import type { TransactionFilters, View } from "./views";

import type { OverlayState } from "./use-app-overlays";

export interface AppOverlaysProps {
  data: DashboardData | null;
  /** Whether `data` holds as much of the ledger as the screen shown needs; overlays that read history wait for it. */
  ready: boolean;
  profile: Owner;
  owner: OwnerFilter;
  view: View;
  month: string;
  serverAvailable: boolean;
  nav: (nextView: View) => void;
  refresh: (force?: boolean) => Promise<DashboardData | undefined>;
  showTransactions: (f: TransactionFilters) => void;
  openMooTransaction: (id: string) => void;
  switchProfile: (p: Owner) => Promise<void>;
  logout: (everywhere?: boolean) => Promise<void>;

  overlays: OverlayState;
  entry: ReturnType<typeof useEntryModal>;
  transactionDetail: ReturnType<typeof useTransactionDetail>;
  reconciliationOpen: boolean;
  setReconciliationOpen: (open: boolean) => void;
  dueReconciliationMonth: string | null;
}

/**
 * Orchestrates all root-shell overlays: detail sheets, creation modals, confirmation dialogs,
 * review flows, the quick-add FAB, and the Moo assistant overlay.
 */
export default function AppOverlays({
  data,
  ready,
  profile,
  owner,
  view,
  month,
  serverAvailable,
  nav,
  refresh,
  showTransactions,
  openMooTransaction,
  switchProfile,
  logout,
  overlays,
  entry,
  transactionDetail,
  reconciliationOpen,
  setReconciliationOpen,
  dueReconciliationMonth,
}: AppOverlaysProps) {
  if (!data) return null;

  const {
    accountDetail,
    setAccountDetail,
    statementAccount,
    setStatementAccount,
    profileMenu,
    setProfileMenu,
    confirm,
    setConfirm,
    setIouFocus,
    setBillFocus,
    setTripFocus,
    setGoalFocus,
    payingBill,
    setPayingBill,
    pendingDetail,
    setPendingDetail,
  } = overlays;
  const { detail, setDetail } = transactionDetail;

  const hasActiveOverlay = Boolean(
    entry.open ||
    profileMenu ||
    detail ||
    accountDetail ||
    statementAccount ||
    reconciliationOpen ||
    confirm ||
    payingBill ||
    pendingDetail
  );

  const liveAccount = data.accounts.find(a => a.id === accountDetail?.id) || accountDetail;
  const selectedStatement =
    liveAccount && isCard(liveAccount)
      ? cardStatementStatus(liveAccount, data.transactions, todayIn(data.timezone))
      : null;

  return (
    <>
      {detail && (
        <TransactionDetail
          {...transactionDetail}
          data={data}
          profile={profile}
          refresh={refresh}
          setConfirm={setConfirm}
          detail={detail}
          onOpenIou={(id) => {
            nav("ious");
            setDetail(null);
            setIouFocus(id);
          }}
          onOpenBill={(id) => {
            nav("bills");
            setDetail(null);
            setBillFocus(id);
          }}
          onOpenGoal={(id) => { setDetail(null); setGoalFocus(id); nav("goals"); }}
          onOpenTrip={(id) => {
            nav("trips");
            setDetail(null);
            setTripFocus(id);
          }}
        />
      )}

      {payingBill && (
        <PayBill
          data={data}
          profile={profile}
          bill={payingBill}
          onClose={() => setPayingBill(null)}
          onPaid={() => refresh(true)}
        />
      )}

      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />

      {liveAccount && ready && (
        <AccountDetail
          account={liveAccount}
          month={view === "accounts" ? month : undefined}
          data={data}
          owner={owner}
          selectedStatement={selectedStatement}
          setAccountDetail={setAccountDetail}
          setStatementAccount={setStatementAccount}
          setDetail={setDetail}
          showTransactions={showTransactions}
          onAddTransaction={(accountId, type, prefill) => {
            setAccountDetail(null);
            entry.show({ accountId, type, ...prefill });
          }}
          onOpenTrip={(tripId) => {
            setAccountDetail(null);
            setTripFocus(tripId);
            nav("trips");
          }}
          onOpenGoal={(goalId) => {
            setAccountDetail(null);
            if (goalId) setGoalFocus(goalId);
            nav("goals");
          }}
          onRefresh={() => refresh(true)}
        />
      )}

      {statementAccount && (
        <Modal title="Credit-card statement" onClose={() => setStatementAccount(null)}>
          <CardStatementForm
            account={statementAccount}
            onCancel={() => setStatementAccount(null)}
            onSaved={async () => {
              await refresh(true);
              setStatementAccount(null);
            }}
          />
        </Modal>
      )}

      {dueReconciliationMonth && (
        <ReconciliationReview
          data={data}
          month={dueReconciliationMonth}
          profile={profile}
          open={reconciliationOpen}
          onClose={() => setReconciliationOpen(false)}
          onSaved={() => refresh(true)}
          onOpenAccount={(accountId) => {
            setReconciliationOpen(false);
            showTransactions({ owner: "All", account: accountId, month: dueReconciliationMonth });
          }}
          onReviewPending={() => {
            setReconciliationOpen(false);
            showTransactions({ owner: "All", type: "Pending" });
          }}
        />
      )}

      <button
        className="add-expense-fab"
        onClick={() => entry.show()}
        aria-label="Add transaction"
      >
        <Plus size={23} />
        <span>Add transaction</span>
      </button>

      <Moo
        key={profile}
        data={data}
        profile={profile}
        online={serverAvailable}
        hidden={hasActiveOverlay}
        context={view}
        onChanged={() => void refresh(true)}
        onOpenTransaction={openMooTransaction}
      />

      {pendingDetail && (
        <PendingTransactionDetail
          key={pendingDetail}
          storageKey={pendingDetail}
          data={data}
          profile={profile}
          onClose={() => setPendingDetail(null)}
        />
      )}

      {entry.open && (
        <AddTransactionModal
          data={data}
          profile={profile}
          preset={entry.preset}
          saved={entry.saved}
          online={serverAvailable}
          onSaved={() => {
            entry.markSaved();
            return refresh(true);
          }}
          onClose={entry.close}
        />
      )}

      {profileMenu && (
        <ProfileMenu
          profile={profile}
          owner={owner}
          view={view}
          switchProfile={switchProfile}
          logout={logout}
          setProfileMenu={setProfileMenu}
        />
      )}
    </>
  );
}
